import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Camera, TrashSimple, MagnifyingGlass, Copy, FunnelSimple, DownloadSimple, PencilSimple, Sparkle } from "@phosphor-icons/react";

const CATS = {
  expenses_patrol: "Expenses - Patrol",
  expenses_hotel:  "Expenses - Hotel",
};

function fmtDate(s){ if(!s) return "—"; if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}
function fmtDT(s){ if(!s) return "—"; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleString("en-GB",{day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"});}
function fmtRM(n){ if(n==null) return <span className="text-zinc-400">—</span>; return `RM ${Number(n).toLocaleString("en-MY",{minimumFractionDigits:2,maximumFractionDigits:2})}`;}

export default function MobileExpenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (cat) qs.set("category", cat);
      if (status) qs.set("status", status);
      const { data } = await api.get("/mobile-expenses" + (qs.toString() ? `?${qs}` : ""));
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, status]);

  const del = async (r) => {
    if (!window.confirm(`Delete this ${CATS[r.category] || r.category} record from ${r.submitted_by_name}?`)) return;
    try { await api.delete(`/mobile-expenses/${r.id}`); setItems(xs => xs.filter(x => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openImage = (id) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${API}/mobile-expenses/${id}/image/download?auth=${encodeURIComponent(t)}`, "_blank");
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return items;
    return items.filter(x => {
      const hay = `${x.ai_vendor||""} ${x.ai_date||""} ${x.ai_notes||""} ${x.submitted_by_name||""} ${x.filename||""}`.toLowerCase();
      return hay.includes(n);
    });
  }, [items, q]);

  const total = useMemo(() => filtered.reduce((s,x)=>s+Number(x.ai_amount||0),0), [filtered]);

  if (user?.role !== "admin") {
    return <Layout><div className="max-w-md mx-auto mt-20 bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">Admin only.</div></Layout>;
  }

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2 flex items-center gap-1">
            <Sparkle size={10} weight="fill" className="text-amber-600"/> AI-extracted · Mobile intake
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Mobile Expenses</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Bill / receipt photos posted from the cargo-fill-mobile app. Each image is auto-scanned by AI for vendor, date, and total RM before it lands here.
          </p>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search vendor, notes, worker, file…" data-testid="mexp-search" className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        </div>
        <select value={cat} onChange={e=>setCat(e.target.value)} data-testid="mexp-cat-filter" className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All categories</option>
          {Object.entries(CATS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={status} onChange={e=>setStatus(e.target.value)} data-testid="mexp-status-filter" className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All statuses</option>
          <option value="pending">Pending confirmation</option>
          <option value="confirmed">Confirmed</option>
        </select>
        <div className="inline-flex items-center gap-2 bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm" data-testid="mexp-total">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Total</span>
          <span className="font-display font-black text-zinc-950">{fmtRM(total)}</span>
          <span className="text-xs text-zinc-500 font-mono">· {filtered.length}</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-left px-4 py-3">Receipt Date</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Notes</th>
              <th className="text-left px-4 py-3">Submitted By</th>
              <th className="text-left px-4 py-3">Submitted At</th>
              <th className="text-left px-4 py-3">Image</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={10} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            : filtered.length === 0 ? <tr><td colSpan={10} className="text-center py-16 text-zinc-500"><Camera size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3"/><div className="font-mono text-sm">{items.length===0?"No mobile expenses yet — waiting for the first submission from the app.":"No records match this filter."}</div></td></tr>
            : filtered.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`mexp-row-${r.id}`}>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-mono border px-2 py-0.5 uppercase ${r.category==="expenses_hotel"?"border-purple-200 bg-purple-50 text-purple-800":"border-orange-200 bg-orange-50 text-orange-800"}`}>{CATS[r.category]||r.category}</span>
                </td>
                <td className="px-4 py-3">
                  {r.status === "confirmed"
                    ? <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-green-200 bg-green-50 text-green-800 px-2 py-0.5 uppercase">Confirmed</span>
                    : <span className="inline-flex items-center gap-1 text-[11px] font-mono border border-amber-200 bg-amber-50 text-amber-800 px-2 py-0.5 uppercase">Pending</span>}
                </td>
                <td className="px-4 py-3 text-zinc-900 font-medium truncate max-w-[180px]">{r.ai_vendor || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(r.ai_date)}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-900 whitespace-nowrap">{fmtRM(r.ai_amount)}</td>
                <td className="px-4 py-3 text-zinc-700 truncate max-w-[220px]" title={r.ai_notes}>{r.ai_notes || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-800 truncate max-w-[140px]">{r.submitted_by_name || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 font-mono text-zinc-600 whitespace-nowrap text-xs">{fmtDT(r.submitted_at)}</td>
                <td className="px-4 py-3">
                  <button onClick={()=>openImage(r.id)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-sm" data-testid={`mexp-view-${r.id}`}>
                    <Camera size={14} weight="duotone"/> <span className="truncate max-w-[140px]">{r.filename || "receipt"}</span>
                  </button>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={()=>openImage(r.id)} data-testid={`mexp-download-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50"><DownloadSimple size={14}/> Image</button>
                  <button onClick={()=>del(r)} data-testid={`mexp-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 ml-1"><TrashSimple size={14}/> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
